import React, { useState, useEffect } from 'react';
import './AstrologerApplication.css';
import { API_CONFIG } from '../config/apiConfig';
import { apiGet, apiPost, parseJsonResponse } from '../utils/apiClient';

const AstrologerApplication = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    lastName: '',
    displayName: '',
    dateOfBirth: '',
    email: '',
    phone: '',
    phoneCode: '+91',
    password: '',
    emailOtp: '',
    phoneOtp: '',
    emailVerified: false,
    phoneVerified: false,
    emailOtpSent: false,
    phoneOtpSent: false,
    languages: [],
    specializations: [],
    primarySkills: [],
    experience: '',
    otherApps: [],
    bioEnglish: '',
    bioHindi: '',
    bankName: '',
    bankAccount: '',
    ifsc: '',
    hoursPerDay: '',
    profileImage: null,
    galleryImages: [],
    agreeTerms: false
  });

  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState('');
  const [generatingBio, setGeneratingBio] = useState(false);
  const [displayNameManuallyEdited, setDisplayNameManuallyEdited] = useState(false);
  const [apiData, setApiData] = useState({
    languages: [],
    specializations: [],
    primarySkills: [],
    otherApps: []
  });

  // Fetch data from API on component mount
  useEffect(() => {
    const fetchAstrologerData = async () => {
      try {
        setLoading(true);
        const response = await apiGet(API_CONFIG.ENDPOINTS.GET_ASTROLOGER_DATA);
        
        if (!response.ok) {
          throw new Error(`Failed to fetch astrologer data: ${response.status} ${response.statusText}`);
        }
        
        const result = await parseJsonResponse(response);
        
        if (result.status === 'success' && result.data) {
          setApiData({
            languages: result.data.languages || [],
            specializations: result.data.tags?.filter(tag => tag.type === 2) || [],
            primarySkills: result.data.categories || [],
            otherApps: result.data.other_apps || []
          });
        }
      } catch (error) {
        console.error('Error fetching astrologer data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAstrologerData();
  }, []);

  const experienceOptions = ['0-5', '5-10', '10-15', '15-20', '20+'];

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Auto-fill display name when first name is entered
    if (name === 'fullName' && value.trim() && !displayNameManuallyEdited) {
      const firstName = value.trim();
      const capitalizedFirstName = firstName.charAt(0).toUpperCase() + firstName.slice(1).toLowerCase();
      setFormData(prev => ({
        ...prev,
        [name]: value,
        displayName: `Astro ${capitalizedFirstName}`
      }));
    } else if (name === 'displayName') {
      // Mark display name as manually edited when user changes it
      setDisplayNameManuallyEdited(true);
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
    
    // Clear field error when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
    
    // Clear general error when user starts typing
    if (error) {
      setError('');
    }
  };

  const toggleSelection = (field, value) => {
    setFormData(prev => {
      const current = prev[field] || [];

      // Limit primary skills to maximum 3 selections
      if (field === 'primarySkills' && !current.includes(value) && current.length >= 3) {
        setFieldErrors(prev => ({ ...prev, primarySkills: 'You can select a maximum of 3 primary skills' }));
        return prev;
      }

      // Clear error when selection changes
      if (fieldErrors[field]) {
        setFieldErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[field];
          return newErrors;
        });
      }

      if (current.includes(value)) {
        return { ...prev, [field]: current.filter(item => item !== value) };
      } else {
        return { ...prev, [field]: [...current, value] };
      }
    });
  };

  const handleSpecializationChange = (spec) => {
    setFormData(prev => {
      const current = prev.specializations || [];
      
      // If unchecking, allow it
      if (current.includes(spec)) {
        return { ...prev, specializations: current.filter(item => item !== spec) };
      } 
      // If checking, limit to maximum 4
      else if (current.length >= 4) {
        setFieldErrors(prev => ({ ...prev, specializations: 'You can select a maximum of 4 specializations' }));
        return prev;
      } 
      // Add the specialization
      else {
        return { ...prev, specializations: [...current, spec] };
      }
    });
    
    // Clear error when specialization changes
    if (fieldErrors.specializations) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.specializations;
        return newErrors;
      });
    }
  };

  const sendEmailOtp = async () => {
    if (!formData.email) {
      setFieldErrors(prev => ({ ...prev, email: 'Please enter your email address' }));
      return;
    }

    try {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.email;
        return newErrors;
      });
      const formDataToSend = new FormData();
      formDataToSend.append('email', formData.email);

      const response = await apiPost(API_CONFIG.ENDPOINTS.SEND_EMAIL_OTP, formDataToSend);
      const result = await parseJsonResponse(response);

      if (result.status === 'success' || response.ok) {
        setFormData(prev => ({ ...prev, emailOtpSent: true }));
        setSuccessMessage('OTP sent to your email');
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setFieldErrors(prev => ({ ...prev, email: result.message || 'Failed to send OTP' }));
      }
    } catch (error) {
      console.error('Error sending email OTP:', error);
      setFieldErrors(prev => ({ ...prev, email: 'Failed to send OTP. Please try again.' }));
    }
  };

  const verifyEmailOtp = async () => {
    if (!formData.emailOtp) {
      setFieldErrors(prev => ({ ...prev, emailOtp: 'Please enter the OTP' }));
      return;
    }

    try {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.emailOtp;
        return newErrors;
      });
      const formDataToSend = new FormData();
      formDataToSend.append('email', formData.email);
      formDataToSend.append('otp', formData.emailOtp);

      const response = await apiPost(API_CONFIG.ENDPOINTS.VERIFY_EMAIL_OTP, formDataToSend);
      const result = await parseJsonResponse(response);

      if (result.status === 'success' || response.ok) {
        setFormData(prev => ({ ...prev, emailVerified: true }));
        setSuccessMessage('Email verified successfully');
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setFieldErrors(prev => ({ ...prev, emailOtp: result.message || 'Invalid OTP' }));
      }
    } catch (error) {
      console.error('Error verifying email OTP:', error);
      setFieldErrors(prev => ({ ...prev, emailOtp: 'Failed to verify OTP. Please try again.' }));
    }
  };

  const sendPhoneOtp = async () => {
    if (!formData.phone) {
      setFieldErrors(prev => ({ ...prev, phone: 'Please enter your phone number' }));
      return;
    }

    try {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.phone;
        return newErrors;
      });
      const formDataToSend = new FormData();
      formDataToSend.append('phone', formData.phone);
      formDataToSend.append('country_code', formData.phoneCode);

      const response = await apiPost(API_CONFIG.ENDPOINTS.SEND_PHONE_OTP, formDataToSend);
      const result = await parseJsonResponse(response);

      if (result.status === 'success' || response.ok) {
        setFormData(prev => ({ ...prev, phoneOtpSent: true }));
        setSuccessMessage('OTP sent to your phone');
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setFieldErrors(prev => ({ ...prev, phone: result.message || 'Failed to send OTP' }));
      }
    } catch (error) {
      console.error('Error sending phone OTP:', error);
      setFieldErrors(prev => ({ ...prev, phone: 'Failed to send OTP. Please try again.' }));
    }
  };

  const verifyPhoneOtp = async () => {
    if (!formData.phoneOtp) {
      setFieldErrors(prev => ({ ...prev, phoneOtp: 'Please enter the OTP' }));
      return;
    }

    try {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.phoneOtp;
        return newErrors;
      });
      const formDataToSend = new FormData();
      formDataToSend.append('phone', formData.phone);
      formDataToSend.append('country_code', formData.phoneCode);
      formDataToSend.append('otp', formData.phoneOtp);

      const response = await apiPost(API_CONFIG.ENDPOINTS.VERIFY_PHONE_OTP, formDataToSend);
      const result = await parseJsonResponse(response);

      if (result.status === 'success' || response.ok) {
        setFormData(prev => ({ ...prev, phoneVerified: true }));
        setSuccessMessage('Phone verified successfully');
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setFieldErrors(prev => ({ ...prev, phoneOtp: result.message || 'Invalid OTP' }));
      }
    } catch (error) {
      console.error('Error verifying phone OTP:', error);
      // If phone-verify endpoint doesn't exist, you may need to verify through send-otp
      // or use a different endpoint. For now, we'll show the error.
      setFieldErrors(prev => ({ ...prev, phoneOtp: 'Failed to verify OTP. Please check if the endpoint exists or contact support.' }));
    }
  };

  const uploadImage = async (imageFile) => {
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('image', imageFile);

      const response = await apiPost(API_CONFIG.ENDPOINTS.UPLOAD_IMAGE, formDataToSend);
      const result = await parseJsonResponse(response);

      if (result.status === 'success' && result.data) {
        return result.data.image_url || result.data.url || result.data.path;
      } else {
        throw new Error(result.message || 'Failed to upload image');
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      throw error;
    }
  };

  // Helper function to extract filename from URL
  const extractFilenameFromUrl = (url) => {
    if (!url) return '';
    try {
      // Handle both full URLs and relative paths
      const urlObj = new URL(url);
      const pathname = urlObj.pathname;
      // Extract filename from pathname (last part after /)
      const filename = pathname.split('/').pop();
      return filename || url.split('/').pop();
    } catch (e) {
      // If URL parsing fails, try to extract from string
      const parts = url.split('/');
      return parts[parts.length - 1] || url;
    }
  };

  // Helper function to extract maximum value from hours range
  // Examples: "0-2" -> "2", "2-4" -> "4", "12+" -> "12"
  const extractMaxHours = (hoursRange) => {
    if (!hoursRange) return '';
    // Handle "12+" case
    if (hoursRange.includes('+')) {
      return hoursRange.replace('+', '');
    }
    // Handle range like "0-2", "2-4", etc.
    if (hoursRange.includes('-')) {
      const parts = hoursRange.split('-');
      return parts[parts.length - 1]; // Return the last part (maximum value)
    }
    // If no range format, return as is
    return hoursRange;
  };

  const handleImageChange = (e, type = 'profile') => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      // Validate file type - silently skip if not image
      if (!file.type.startsWith('image/')) {
        return;
      }
      
      // Clear error when valid file is selected
      const fieldName = type === 'profile' ? 'profileImage' : 'galleryImages';
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
      
      if (type === 'profile') {
        setFormData(prev => ({ ...prev, profileImage: file }));
      } else if (type === 'gallery') {
        const newImages = Array.from(files).filter(file => file.type.startsWith('image/'));
        setFormData(prev => {
          const currentImages = prev.galleryImages || [];
          const remainingSlots = 3 - currentImages.length;
          const imagesToAdd = newImages.slice(0, remainingSlots);
          const updatedImages = [...currentImages, ...imagesToAdd];
          // Clear error if we now have 3 images
          if (updatedImages.length === 3) {
            setFieldErrors(prev => {
              const newErrors = { ...prev };
              delete newErrors.galleryImages;
              return newErrors;
            });
          }
          return { ...prev, galleryImages: updatedImages };
        });
      }
    }
    // Reset input so same file can be selected again
    e.target.value = '';
  };

  const removeGalleryImage = (index) => {
    setFormData(prev => ({
      ...prev,
      galleryImages: prev.galleryImages.filter((_, i) => i !== index)
    }));
  };

  const generateBioWithAI = async () => {
    console.log('generateBioWithAI called');
    console.log('Form data:', {
      fullName: formData.fullName,
      displayName: formData.displayName,
      specializations: formData.specializations,
      apiDataSpecializations: apiData.specializations
    });

    // Validate required fields for bio generation
    const newFieldErrors = {};
    let hasErrors = false;

    if (!formData.fullName.trim()) {
      newFieldErrors.fullName = 'First name is required to generate bio';
      hasErrors = true;
    }

    if (!formData.displayName.trim()) {
      newFieldErrors.displayName = 'Display name is required to generate bio';
      hasErrors = true;
    }

    if (hasErrors) {
      setFieldErrors(prev => ({ ...prev, ...newFieldErrors }));
      // Scroll to the first error field (fullName or displayName)
      setTimeout(() => {
        scrollToFirstError(newFieldErrors);
      }, 100);
      return;
    }

    // Get specializations from form data
    const specializations = formData.specializations.map(id => {
      const spec = apiData.specializations.find(s => s.id === id);
      return spec ? spec.name : '';
    }).filter(Boolean);

    console.log('Specializations found:', specializations);

    if (specializations.length === 0) {
      console.log('Validation failed: No specializations selected');
      const specError = { specializations: 'Please select at least one specialization to generate bio' };
      setFieldErrors(prev => ({ ...prev, ...specError }));
      // Scroll to specializations field
      setTimeout(() => {
        scrollToFirstError(specError);
      }, 100);
      return;
    }

    try {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.displayName;
        delete newErrors.specializations;
        return newErrors;
      });
      setGeneratingBio(true);

      // Get API key from config
      const apiKey = API_CONFIG.GEMINI_API_KEY;
      const modelName = API_CONFIG.GEMINI_MODEL;
      
      console.log('API Key configured:', apiKey ? 'Yes (length: ' + apiKey.length + ')' : 'No');
      console.log('Model name:', modelName);
      
      if (!apiKey) {
        console.error('GEMINI_API_KEY is not configured');
        setFieldErrors(prev => ({ ...prev, bioEnglish: 'AI service is not configured. Please check API configuration.' }));
        setGeneratingBio(false);
        return;
      }

      // Prepare name and experience
      const astroName = formData.displayName || formData.fullName;
      const experience = formData.experience || 'several';

      // Create prompt similar to the example
      const prompt = `Write a short, warm, and professional astrologer biography (first person, under 60 words) for someone named "${astroName}".  

Specializations: ${specializations.join(', ')}.  

Experience: ${experience} years. 

Tone: Mystical but grounded, empathetic, and trustworthy.  

IMPORTANT: Do NOT use any emojis. Keep the text clean. 

Provide response in JSON format with keys "english" and "hindi".`;

      // Call Google Gemini API - use v1 instead of v1beta
      const apiVersion = 'v1';
      
      const apiUrl = `https://generativelanguage.googleapis.com/${apiVersion}/models/${modelName}:generateContent?key=${apiKey}`;
      
      console.log('Calling Gemini API with model:', modelName, 'API version:', apiVersion);
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          contents: [{ parts: [{ text: prompt }] }]
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Gemini API error:', errorText);
        
        // Try to parse error for better message
        try {
          const errorJson = JSON.parse(errorText);
          if (errorJson.error && errorJson.error.message) {
            console.error('Detailed error:', errorJson.error.message);
            throw new Error(errorJson.error.message);
          }
        } catch (parseErr) {
          // If we can't parse the error, use the original error
        }
        
        throw new Error(`AI service error: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      
      // Parse the generated JSON from Gemini
      const generatedText = data.candidates?.[0]?.content?.parts?.[0]?.text || '';
      
      if (!generatedText) {
        throw new Error('No response from AI service');
      }

      let generatedJson;
      try {
        // Try to parse as JSON (it might be wrapped in markdown code blocks or be an array)
        const cleanedText = generatedText.replace(/```json\n?/g, '').replace(/```\n?/g, '').trim();
        const parsed = JSON.parse(cleanedText);
        
        // Handle both array response [{english: "...", hindi: "..."}] and object {english: "...", hindi: "..."}
        if (Array.isArray(parsed) && parsed.length > 0) {
          generatedJson = parsed[0];
        } else if (typeof parsed === 'object' && parsed !== null) {
          generatedJson = parsed;
        } else {
          throw new Error('Unexpected response format');
        }
      } catch (parseError) {
        console.error('Failed to parse AI response:', generatedText);
        console.error('Parse error:', parseError);
        throw new Error('Invalid response format from AI service');
      }

      // Update both bio fields if we have the data
      if (generatedJson.english || generatedJson.hindi) {
        setFormData(prev => ({
          ...prev,
          bioEnglish: generatedJson.english || prev.bioEnglish,
          bioHindi: generatedJson.hindi || prev.bioHindi
        }));
        setSuccessMessage('Bio generated successfully! You can edit it if needed.');
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        throw new Error('Bio generation incomplete - missing english or hindi fields');
      }
    } catch (error) {
      console.error('Error generating bio:', error);
      setFieldErrors(prev => ({ ...prev, bioEnglish: `Failed to generate bio: ${error.message}. Please try again or write manually.` }));
    } finally {
      // Reset loading state
      setGeneratingBio(false);
    }
  };

  // Function to scroll to the first error field
  const scrollToFirstError = (errors) => {
    // Define the order of fields as they appear in the form
    const fieldOrder = [
      'emailVerified',
      'phoneVerified',
      'profileImage',
      'galleryImages',
      'fullName',
      'lastName',
      'displayName',
      'dateOfBirth',
      'password',
      'email',
      'phone',
      'primarySkills',
      'specializations',
      'experience',
      'bioEnglish',
      'bioHindi',
      'bankAccount',
      'ifsc',
      'hoursPerDay',
      'agreeTerms'
    ];

    // Find the first error field based on the order
    let firstErrorField = null;
    for (const fieldName of fieldOrder) {
      if (errors[fieldName]) {
        firstErrorField = fieldName;
        break;
      }
    }

    if (!firstErrorField) return;

    // Handle special cases for fields without standard input elements
    if (firstErrorField === 'profileImage') {
      const profileSection = document.querySelector('.profile-photo-container');
      if (profileSection) {
        profileSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
    }

    if (firstErrorField === 'galleryImages') {
      const gallerySection = document.querySelector('.additional-photos-container');
      if (gallerySection) {
        gallerySection.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
    }

    if (firstErrorField === 'primarySkills') {
      const primarySkillsSection = document.querySelector('.primary-skills-grid');
      if (primarySkillsSection) {
        primarySkillsSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
    }

    if (firstErrorField === 'specializations') {
      const specializationsSection = document.querySelector('.specializations-list');
      if (specializationsSection) {
        specializationsSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
    }

    if (firstErrorField === 'emailVerified') {
      const emailInput = document.querySelector('input[name="email"]');
      if (emailInput) {
        emailInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
        emailInput.focus();
        return;
      }
    }

    if (firstErrorField === 'phoneVerified') {
      const phoneInput = document.querySelector('input[name="phone"]');
      if (phoneInput) {
        phoneInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
        phoneInput.focus();
        return;
      }
    }

    // For standard input fields, find by name attribute
    const inputElement = document.querySelector(`input[name="${firstErrorField}"], select[name="${firstErrorField}"], textarea[name="${firstErrorField}"]`);
    
    if (inputElement) {
      inputElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // Focus the input after a small delay to ensure scroll completes
      setTimeout(() => {
        inputElement.focus();
      }, 300);
    } else {
      // Fallback: try to find by ID
      const elementById = document.getElementById(firstErrorField);
      if (elementById) {
        elementById.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setTimeout(() => {
          if (elementById.focus) {
            elementById.focus();
          }
        }, 300);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setFieldErrors({});
    setSuccessMessage('');

    let hasErrors = false;
    const newFieldErrors = {};

    // Check if email and phone are verified
    if (!formData.emailVerified) {
      newFieldErrors.email = 'Please verify your email address';
      hasErrors = true;
    }

    if (!formData.phoneVerified) {
      newFieldErrors.phone = 'Please verify your phone number';
      hasErrors = true;
    }

    // Validate that all form fields are filled / selected
    if (!formData.profileImage) {
      newFieldErrors.profileImage = 'Please upload your profile photo';
      hasErrors = true;
    }

    if (!formData.galleryImages || formData.galleryImages.length !== 3) {
      newFieldErrors.galleryImages = 'Please add exactly 3 additional photos';
      hasErrors = true;
    }

    if (!formData.fullName.trim()) {
      newFieldErrors.fullName = 'First name is required';
      hasErrors = true;
    }

    if (!formData.lastName.trim()) {
      newFieldErrors.lastName = 'Last name is required';
      hasErrors = true;
    }

    if (!formData.displayName.trim()) {
      newFieldErrors.displayName = 'Display name is required';
      hasErrors = true;
    }

    if (!formData.dateOfBirth) {
      newFieldErrors.dateOfBirth = 'Date of birth is required';
      hasErrors = true;
    }

    if (!formData.password) {
      newFieldErrors.password = 'Password is required';
      hasErrors = true;
    }

    if (!formData.email) {
      newFieldErrors.email = 'Email is required';
      hasErrors = true;
    }

    if (!formData.phone) {
      newFieldErrors.phone = 'Phone number is required';
      hasErrors = true;
    }

    // Languages, specializations, and otherApps are optional - user can select as many as they want
    // Only primary skills is required (max 3)
    // Specializations are optional but limited to max 4
    
    if (!formData.primarySkills || formData.primarySkills.length === 0) {
      newFieldErrors.primarySkills = 'Please select at least one primary skill (maximum 3)';
      hasErrors = true;
    }

    if (formData.primarySkills.length > 3) {
      newFieldErrors.primarySkills = 'You can select a maximum of 3 primary skills';
      hasErrors = true;
    }

    // Validate specializations max limit (4)
    if (formData.specializations && formData.specializations.length > 4) {
      newFieldErrors.specializations = 'You can select a maximum of 4 specializations';
      hasErrors = true;
    }

    if (!formData.experience) {
      newFieldErrors.experience = 'Please select your years of experience';
      hasErrors = true;
    }

    if (!formData.bioEnglish.trim()) {
      newFieldErrors.bioEnglish = 'Please write your bio in English';
      hasErrors = true;
    }

    if (!formData.bioHindi.trim()) {
      newFieldErrors.bioHindi = 'Please write your bio in Hindi';
      hasErrors = true;
    }

    if (!formData.bankAccount.trim()) {
      newFieldErrors.bankAccount = 'Bank account number is required';
      hasErrors = true;
    }

    if (!formData.ifsc.trim()) {
      newFieldErrors.ifsc = 'IFSC code is required';
      hasErrors = true;
    }

    if (!formData.hoursPerDay) {
      newFieldErrors.hoursPerDay = 'Please select how many hours per day you can devote';
      hasErrors = true;
    }

    if (!formData.agreeTerms) {
      newFieldErrors.agreeTerms = 'You must agree to the Terms and Conditions to continue';
      hasErrors = true;
    }

    if (hasErrors) {
      setFieldErrors(newFieldErrors);
      // Scroll to the first error field
      setTimeout(() => {
        scrollToFirstError(newFieldErrors);
      }, 100);
      return;
    }

    try {
      setSubmitting(true);

      // Use fullName as firstName and lastName from form
      const firstName = formData.fullName.trim();
      const lastName = formData.lastName.trim();

      // Upload profile image if exists
      let profileImageUrl = '';
      let profileImageFilename = '';
      if (formData.profileImage) {
        profileImageUrl = await uploadImage(formData.profileImage);
        profileImageFilename = extractFilenameFromUrl(profileImageUrl);
      }

      // Upload gallery images
      const galleryImageUrls = [];
      const galleryImageFilenames = [];
      for (const image of formData.galleryImages) {
        const url = await uploadImage(image);
        galleryImageUrls.push(url);
        galleryImageFilenames.push(extractFilenameFromUrl(url));
      }

      // Prepare registration data
      const registrationData = new FormData();
      registrationData.append('first_name', firstName);
      registrationData.append('last_name', lastName);
      registrationData.append('display_name', formData.displayName.trim());
      registrationData.append('date_of_birth', formData.dateOfBirth);
      registrationData.append('email', formData.email);
      registrationData.append('password', formData.password);
      registrationData.append('phone', formData.phone);
      registrationData.append('country_code', formData.phoneCode);
      
      if (profileImageFilename) {
        registrationData.append('profile_image', profileImageFilename);
      }
      
      registrationData.append('experience', formData.experience);
      
      // Append gallery images as JSON string (backend expects JSON string, not array)
      // Send only filenames, not full URLs
      if (galleryImageFilenames.length > 0) {
        registrationData.append('gallery_images', JSON.stringify(galleryImageFilenames));
      }

      // Append languages (sp_langauges) as JSON string
      if (formData.languages && formData.languages.length > 0) {
        const languagesArray = formData.languages.map(id => String(id));
        registrationData.append('sp_langauges', JSON.stringify(languagesArray));
      }

      // Append categories (primary skills) as JSON string
      if (formData.primarySkills && formData.primarySkills.length > 0) {
        const categoriesArray = formData.primarySkills.map(id => String(id));
        registrationData.append('category', JSON.stringify(categoriesArray));
      }

      // Append tags (specializations) as JSON string
      if (formData.specializations && formData.specializations.length > 0) {
        const tagsArray = formData.specializations.map(id => String(id));
        registrationData.append('tags', JSON.stringify(tagsArray));
      }

      // Append other apps as JSON string
      if (formData.otherApps && formData.otherApps.length > 0) {
        const otherAppsArray = formData.otherApps.map(id => String(id));
        registrationData.append('others_apps', JSON.stringify(otherAppsArray));
      }

      // Append bio fields
      if (formData.bioEnglish) {
        registrationData.append('about_en', formData.bioEnglish.trim());
      }
      if (formData.bioHindi) {
        registrationData.append('about_hi', formData.bioHindi.trim());
      }

      // Append bank details
      if (formData.bankAccount) {
        registrationData.append('bank_account', formData.bankAccount.trim());
      }
      if (formData.ifsc) {
        registrationData.append('ifsc', formData.ifsc.trim());
      }

      // Append hours per day (send only maximum value from range)
      if (formData.hoursPerDay) {
        const maxHours = extractMaxHours(formData.hoursPerDay);
        registrationData.append('hours_per_day', maxHours);
      }

      // Submit registration
      const response = await apiPost(API_CONFIG.ENDPOINTS.ASTROLOGER_REGISTRATION, registrationData);
      const result = await parseJsonResponse(response);

      if (result.status === 'success' || response.ok) {
        setSubmitted(true);
        setSuccessMessage('Application submitted successfully!');
      } else {
        // Show detailed error message from backend
        const errorMsg = result.message || result.error || 'Failed to submit application';
        console.error('Backend error:', result);
        // Try to parse field-specific errors from backend if available
        if (result.errors && typeof result.errors === 'object') {
          setFieldErrors(result.errors);
          // Scroll to the first error field
          setTimeout(() => {
            scrollToFirstError(result.errors);
          }, 100);
        } else {
          setError(errorMsg);
        }
      }
    } catch (error) {
      console.error('Error submitting application:', error);
      setError(`Failed to submit application: ${error.message}. Please try again.`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="astrologer-application-page">
      <div className="application-container">
        {/* Left Section - Promotional */}
        <div className="application-left">
          <div className="promo-badge">
            <span>⭐</span> Join India's Most Trusted Platform
          </div>
          <h1 className="promo-title">
            Share your <span className="highlight">Vedic Wisdom</span> with the world.
          </h1>
          <p className="promo-description">
            Join our community of expert astrologers. We provide the technology, marketing, and client base so you can focus on what you do best—guiding people.
          </p>
          
          {/* <div className="promo-skills">
            <h3 className="promo-skills-title">Primary Skills</h3>
            <div className="promo-skills-grid">
              {apiData.primarySkills.map((skill) => (
                <div key={skill.id} className="promo-skill-tag">
                  {skill.name}
                </div>
              ))}
            </div>
          </div> */}
        </div>

        {/* Right Section - Form */}
        <div className="application-right">
          <div className="form-container">
            <div className="form-header">
              <h2>Astrologer Signup</h2>
              <p className="form-subtitle">Fill in your details to begin the verification process.</p>
            </div>

            {!submitted ? (
              <form onSubmit={handleSubmit} className="application-form">
                {/* Error and Success Messages */}
                {error && (
                  <div className="alert alert-error" style={{ padding: '12px', marginBottom: '20px', backgroundColor: '#fee', color: '#c33', borderRadius: '4px' }}>
                    {error}
                  </div>
                )}
                {successMessage && (
                  <div className="alert alert-success" style={{ padding: '12px', marginBottom: '20px', backgroundColor: '#efe', color: '#3c3', borderRadius: '4px' }}>
                    {successMessage}
                  </div>
                )}

                {/* Photos Section */}
                <div className="form-section">
                  <label className="section-label">PHOTOS(AI Photos Will be Rejected)</label>
                  
                  <div className="profile-photo-container">
                    <div 
                      className="profile-photo-circle"
                      onClick={() => document.getElementById('profile-image-input').click()}
                    >
                      <input
                        id="profile-image-input"
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageChange(e, 'profile')}
                        style={{ display: 'none' }}
                      />
                      {formData.profileImage ? (
                        <img 
                          src={URL.createObjectURL(formData.profileImage)} 
                          alt="Profile" 
                          className="profile-photo-preview"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      ) : (
                        <span className="profile-photo-text">Profile Photo</span>
                      )}
                    </div>
                    <div className="profile-photo-actions">
                      <button 
                        type="button"
                        className="choose-file-btn"
                        onClick={() => document.getElementById('profile-image-input').click()}
                      >
                        Choose File
                      </button>
                      <span className="file-chosen-text">
                        {formData.profileImage ? formData.profileImage.name : 'No file chosen'}
                      </span>
                      <p className="file-hint">Must be clearly visible</p>
                    </div>
                    {fieldErrors.profileImage && (
                      <div className="field-error">{fieldErrors.profileImage}</div>
                    )}
                  </div>

                  <div className="additional-photos-container">
                    <label className="additional-photos-label">Additional Photos (Required: 3 photos)  (AI Photos Will be Rejected)</label>
                    <div className="additional-photos-grid">
                      {formData.galleryImages.slice(0, 3).map((img, index) => (
                        <div key={index} className="additional-photo-item">
                          <img 
                            src={URL.createObjectURL(img)} 
                            alt={`Gallery ${index + 1}`} 
                            className="additional-photo-preview"
                          />
                          <button
                            type="button"
                            className="remove-photo-btn"
                            onClick={() => removeGalleryImage(index)}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                      {formData.galleryImages.length < 3 && (
                        <div 
                          className="additional-photo-add"
                          onClick={() => document.getElementById('gallery-images-input').click()}
                        >
                          <input
                            id="gallery-images-input"
                            type="file"
                            accept="image/*"
                            multiple
                            onChange={(e) => handleImageChange(e, 'gallery')}
                            style={{ display: 'none' }}
                          />
                          <span className="add-icon">+</span>
                          <span className="add-text">Add</span>
                        </div>
                      )}
                    </div>
                    {fieldErrors.galleryImages && (
                      <div className="field-error">{fieldErrors.galleryImages}</div>
                    )}
                  </div>
                </div>

                {/* Identity & Contact */}
                <div className="form-section">
                  <label className="section-label">IDENTITY & CONTACT</label>
                  
                  <div className="form-section-grid">
                    <div className="form-group">
                      <label>First Name</label>
                      <input
                        type="text"
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleInputChange}
                        placeholder="Real Name"
                        required
                        className={fieldErrors.fullName ? 'input-error' : ''}
                      />
                      {fieldErrors.fullName && (
                        <div className="field-error">{fieldErrors.fullName}</div>
                      )}
                    </div>

                    <div className="form-group">
                      <label>Last Name</label>
                      <input
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        placeholder="Surname"
                        required
                        className={fieldErrors.lastName ? 'input-error' : ''}
                      />
                      {fieldErrors.lastName && (
                        <div className="field-error">{fieldErrors.lastName}</div>
                      )}
                    </div>

                    <div className="form-group">
                      <label>Display Name</label>
                      <input
                        type="text"
                        name="displayName"
                        value={formData.displayName}
                        onChange={handleInputChange}
                        placeholder="Astro Pandit"
                        required
                        className={fieldErrors.displayName ? 'input-error' : ''}
                      />
                      {fieldErrors.displayName && (
                        <div className="field-error">{fieldErrors.displayName}</div>
                      )}
                    </div>

                    <div className="form-group">
                      <label>Date of Birth</label>
                      <div className="date-input-wrapper">
                        <input
                          type="date"
                          name="dateOfBirth"
                          value={formData.dateOfBirth}
                          onChange={handleInputChange}
                          className={`date-input ${fieldErrors.dateOfBirth ? 'input-error' : ''}`}
                          required
                        />
                        <span className="calendar-icon">📅</span>
                      </div>
                      {fieldErrors.dateOfBirth && (
                        <div className="field-error">{fieldErrors.dateOfBirth}</div>
                      )}
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Password</label>
                    <input
                      type="password"
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      placeholder="Enter password"
                      required
                      className={fieldErrors.password ? 'input-error' : ''}
                    />
                    {fieldErrors.password && (
                      <div className="field-error">{fieldErrors.password}</div>
                    )}
                  </div>

                  <div className="form-group">
                    <label>Work Email {formData.emailVerified && <span style={{ color: '#0E9F6E' }}>✓ Verified</span>}</label>
                    <div className="input-with-button">
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="you@example.com"
                        required
                        disabled={formData.emailVerified}
                        className={fieldErrors.email ? 'input-error' : ''}
                      />
                      <button 
                        type="button" 
                        className="otp-button"
                        onClick={sendEmailOtp}
                        disabled={formData.emailOtpSent || formData.emailVerified}
                      >
                        {formData.emailOtpSent ? 'OTP Sent' : 'Send OTP'}
                      </button>
                    </div>
                    {fieldErrors.email && (
                      <div className="field-error">{fieldErrors.email}</div>
                    )}
                    {formData.emailOtpSent && !formData.emailVerified && (
                      <div style={{ marginTop: '10px', display: 'flex', gap: '10px', flexDirection: 'column' }}>
                        <div style={{ display: 'flex', gap: '10px' }}>
                          <input
                            type="text"
                            name="emailOtp"
                            value={formData.emailOtp}
                            onChange={handleInputChange}
                            placeholder="Enter OTP"
                            style={{ flex: 1, padding: '8px' }}
                            maxLength="6"
                            className={fieldErrors.emailOtp ? 'input-error' : ''}
                          />
                          <button 
                            type="button" 
                            className="otp-button"
                            onClick={verifyEmailOtp}
                          >
                            Verify
                          </button>
                        </div>
                        {fieldErrors.emailOtp && (
                          <div className="field-error">{fieldErrors.emailOtp}</div>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="form-group">
                    <label>Phone Number {formData.phoneVerified && <span style={{ color: '#0E9F6E' }}>✓ Verified</span>}</label>
                    <div className="phone-input-group">
                      <select
                        name="phoneCode"
                        value={formData.phoneCode}
                        onChange={handleInputChange}
                        className="phone-code-select"
                        disabled={formData.phoneVerified}
                      >
                        <option value="+91">+91</option>
                        <option value="+1">+1</option>
                        <option value="+44">+44</option>
                      </select>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder="98765 43210"
                        className={`phone-number-input ${fieldErrors.phone ? 'input-error' : ''}`}
                        required
                        disabled={formData.phoneVerified}
                      />
                      <button 
                        type="button" 
                        className="otp-button"
                        onClick={sendPhoneOtp}
                        disabled={formData.phoneOtpSent || formData.phoneVerified}
                      >
                        {formData.phoneOtpSent ? 'OTP Sent' : 'Send OTP'}
                      </button>
                    </div>
                    {fieldErrors.phone && (
                      <div className="field-error">{fieldErrors.phone}</div>
                    )}
                    {formData.phoneOtpSent && !formData.phoneVerified && (
                      <div style={{ marginTop: '10px', display: 'flex', gap: '10px', flexDirection: 'column' }}>
                        <div style={{ display: 'flex', gap: '10px' }}>
                          <input
                            type="text"
                            name="phoneOtp"
                            value={formData.phoneOtp}
                            onChange={handleInputChange}
                            placeholder="Enter OTP"
                            style={{ flex: 1, padding: '8px' }}
                            maxLength="6"
                            className={fieldErrors.phoneOtp ? 'input-error' : ''}
                          />
                          <button 
                            type="button" 
                            className="otp-button"
                            onClick={verifyPhoneOtp}
                          >
                            Verify
                          </button>
                        </div>
                        {fieldErrors.phoneOtp && (
                          <div className="field-error">{fieldErrors.phoneOtp}</div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Professional Profile */}
                <div className="form-section">
                  <label className="section-label">PROFESSIONAL PROFILE</label>
                  
                  <div className="form-group">
                    <label>Languages Spoken</label>
                    <div className="language-tags">
                      {loading ? (
                        <div>Loading languages...</div>
                      ) : (
                        apiData.languages.map((lang) => (
                          <button
                            key={lang.id}
                            type="button"
                            className={`language-tag ${formData.languages.includes(lang.id) ? 'active' : ''}`}
                            onClick={() => toggleSelection('languages', lang.id)}
                          >
                            {lang.language_title || lang.language}
                          </button>
                        ))
                      )}
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Specializations {formData.specializations.length > 0 && <span className="selection-count">({formData.specializations.length}/4)</span>}</label>
                    <div className="specializations-list">
                      {loading ? (
                        <div>Loading specializations...</div>
                      ) : (
                        apiData.specializations.map((spec) => {
                          const isSelected = formData.specializations.includes(spec.id);
                          const isDisabled = !isSelected && formData.specializations.length >= 4;
                          return (
                            <label 
                              key={spec.id} 
                              className={`specialization-checkbox ${isDisabled ? 'disabled' : ''}`}
                            >
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => handleSpecializationChange(spec.id)}
                                disabled={isDisabled}
                              />
                              <span>{spec.name}</span>
                            </label>
                          );
                        })
                      )}
                    </div>
                    {fieldErrors.specializations && (
                      <div className="field-error">{fieldErrors.specializations}</div>
                    )}
                  </div>

                  <div className="form-group">
                    <label>Primary Skills</label>
                    <div className="primary-skills-grid">
                      {loading ? (
                        <div>Loading skills...</div>
                      ) : (
                        apiData.primarySkills.map((skill) => (
                          <button
                            key={skill.id}
                            type="button"
                            className={`primary-skill-tag ${formData.primarySkills.includes(skill.id) ? 'active' : ''}`}
                            onClick={() => toggleSelection('primarySkills', skill.id)}
                          >
                            {skill.name}
                          </button>
                        ))
                      )}
                    </div>
                    {fieldErrors.primarySkills && (
                      <div className="field-error">{fieldErrors.primarySkills}</div>
                    )}
                  </div>

                  <div className="form-group">
                    <label>Years of Experience</label>
                    <select
                      name="experience"
                      value={formData.experience}
                      onChange={handleInputChange}
                      className={`form-select ${fieldErrors.experience ? 'input-error' : ''}`}
                    >
                      <option value="">Select Experience</option>
                      {experienceOptions.map((exp) => (
                        <option key={exp} value={exp}>{exp} years</option>
                      ))}
                    </select>
                    {fieldErrors.experience && (
                      <div className="field-error">{fieldErrors.experience}</div>
                    )}
                  </div>

                  <div className="form-group">
                    <label>Are you on any other app? (Select all that apply)</label>
                    <div className="other-apps-grid">
                      {loading ? (
                        <div>Loading other apps...</div>
                      ) : (
                        apiData.otherApps.map((app) => (
                          <button
                            key={app.id}
                            type="button"
                            className={`other-app-tag ${formData.otherApps.includes(app.id) ? 'active' : ''}`}
                            onClick={() => toggleSelection('otherApps', app.id)}
                          >
                            {app.name}
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                </div>

                {/* Bio Section */}
                <div className="form-section">
                  <label className="section-label">BIO / ABOUT ME</label>
                  
                  <div className="form-group">
                    <div className="bio-label-with-button">
                      <label>Bio in English</label>
                      <button
                        type="button"
                        className="ai-generate-btn"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          console.log('Generate Bio button clicked');
                          generateBioWithAI();
                        }}
                        disabled={generatingBio}
                      >
                        {generatingBio ? (
                          <>
                            <span className="ai-loading-spinner"></span>
                            Generating Bio...
                          </>
                        ) : (
                          <>
                            <span className="ai-icon">✨</span>
                            Write with AI
                          </>
                        )}
                      </button>
                    </div>
                    <textarea
                      name="bioEnglish"
                      value={formData.bioEnglish}
                      onChange={handleInputChange}
                      rows="4"
                      placeholder="Write your bio in English..."
                      className={fieldErrors.bioEnglish ? 'input-error' : ''}
                    />
                    {fieldErrors.bioEnglish && (
                      <div className="field-error">{fieldErrors.bioEnglish}</div>
                    )}
                  </div>

                  <div className="form-group">
                    <label>Bio in Hindi</label>
                    <textarea
                      name="bioHindi"
                      value={formData.bioHindi}
                      onChange={handleInputChange}
                      rows="4"
                      placeholder="Write your bio in Hindi..."
                      className={fieldErrors.bioHindi ? 'input-error' : ''}
                    />
                    {fieldErrors.bioHindi && (
                      <div className="field-error">{fieldErrors.bioHindi}</div>
                    )}
                  </div>
                </div>

                {/* Final Details */}
                <div className="form-section">
                  <label className="section-label">FINAL DETAILS</label>
                  
                  <div className="form-group">
                    <label>Bank Details</label>
                    <div className="bank-details-wrapper">
                     
                      <div className="bank-inputs">
                        <input
                          type="text"
                          name="bankAccount"
                          value={formData.bankAccount}
                          onChange={handleInputChange}
                          placeholder="Account Number"
                          className={`bank-account-input ${fieldErrors.bankAccount ? 'input-error' : ''}`}
                        />
                        <input
                          type="text"
                          name="ifsc"
                          value={formData.ifsc}
                          onChange={handleInputChange}
                          placeholder="IFSC Code"
                          className={`ifsc-input ${fieldErrors.ifsc ? 'input-error' : ''}`}
                        />
                      </div>
                    </div>
                    {fieldErrors.bankAccount && (
                      <div className="field-error">{fieldErrors.bankAccount}</div>
                    )}
                    {fieldErrors.ifsc && (
                      <div className="field-error">{fieldErrors.ifsc}</div>
                    )}
                  </div>

                  <div className="form-group">
                    <label>How many hours per day can you devote?</label>
                    <select
                      name="hoursPerDay"
                      value={formData.hoursPerDay}
                      onChange={handleInputChange}
                      className={`form-select hours-select ${fieldErrors.hoursPerDay ? 'input-error' : ''}`}
                    >
                      <option value="">Select Hours</option>
                      <option value="0-2">0-2 hours</option>
                      <option value="2-4">2-4 hours</option>
                      <option value="4-8">4-8 hours</option>
                      <option value="8-10">8-10 hours</option>
                      <option value="10-12">10-12 hours</option>
                      <option value="12+">12+ hours</option>
                    </select>
                    {fieldErrors.hoursPerDay && (
                      <div className="field-error">{fieldErrors.hoursPerDay}</div>
                    )}
                  </div>
                </div>

                {/* Terms */}
                <div className="form-group checkbox-group">
                  <input
                    type="checkbox"
                    id="agreeTerms"
                    name="agreeTerms"
                    checked={formData.agreeTerms}
                    onChange={handleInputChange}
                    required
                  />
                  <label htmlFor="agreeTerms">
                    I agree to the{' '}
                    <a 
                      href="https://astroindia.netsolutionindia.com/terms-conditions" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="terms-link"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Terms and Conditions
                    </a>
                    . I confirm the details provided are accurate.
                  </label>
                  {fieldErrors.agreeTerms && (
                    <div className="field-error">{fieldErrors.agreeTerms}</div>
                  )}
                </div>

                <button 
                  type="submit" 
                  className="submit-btn"
                  disabled={!formData.emailVerified || !formData.phoneVerified || submitting}
                  style={{
                    opacity: (!formData.emailVerified || !formData.phoneVerified) ? 0.6 : 1,
                    cursor: (!formData.emailVerified || !formData.phoneVerified) ? 'not-allowed' : 'pointer'
                  }}
                >
                  {submitting ? 'Submitting...' : 'Submit Application'} <span>→</span>
                </button>
                {(!formData.emailVerified || !formData.phoneVerified) && (
                  <p style={{ marginTop: '10px', fontSize: '14px', color: '#c33', textAlign: 'center' }}>
                    Please verify your email and phone number to submit the form
                  </p>
                )}
              </form>
            ) : (
              <div className="success-message">
                <div className="success-icon">✓</div>
                <h2>Application Received!</h2>
                <p>Thank you, {formData.fullName} {formData.lastName}. Our team will review your profile and contact you on <strong>{formData.phoneCode} {formData.phone}</strong> shortly for the interview round.</p>
                <button onClick={() => window.location.reload()} className="back-btn">
                  Back to Home
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AstrologerApplication;
